import React from 'react';
import { AchievementProgress as AchievementProgressType } from '../types/mundus';

interface AchievementProgressProps {
  progress: AchievementProgressType;
}

export const AchievementProgress: React.FC<AchievementProgressProps> = ({ progress }) => {
  const getTierIcon = (tier: string): string => {
    switch (tier) {
      case 'bronze':
        return '🥉';
      case 'silver':
        return '🥈';
      case 'gold':
        return '🥇';
      case 'platinum':
        return '💎';
      default:
        return '🏆';
    }
  };

  const getCategoryIcon = (category: string): string => {
    switch (category) {
      case 'milestone':
        return '🏁';
      case 'streak':
        return '🔥';
      case 'collection':
        return '📦';
      case 'mastery':
        return '⭐';
      case 'progress':
        return '📈';
      case 'special':
        return '✨';
      default:
        return '🏆';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Achievement Progress</h2>

      {/* Overall Progress */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Overall Progress</span>
          <span className="text-sm font-semibold text-indigo-600">
            {progress.unlocked} / {progress.total}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
          <div
            className="bg-gradient-to-r from-indigo-500 to-purple-600 h-3 rounded-full transition-all duration-500"
            style={{ width: `${progress.percentage}%` }}
          />
        </div>
        <p className="text-xs text-gray-500 mt-1">{progress.percentage.toFixed(1)}% Complete</p>
      </div>

      {/* Points */}
      <div className="mb-6 p-4 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">Total Points</p>
            <p className="text-2xl font-bold text-indigo-600">{progress.earnedPoints}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">Available</p>
            <p className="text-lg font-semibold text-gray-700">{progress.totalPoints}</p>
          </div>
        </div>
      </div>

      {/* By Tier */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">By Tier</h3>
        <div className="space-y-2">
          {progress.byTier && Object.entries(progress.byTier).map(([tier, stats]) => (
            <div key={tier} className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-lg">{getTierIcon(tier)}</span>
                <span className="text-sm text-gray-700 capitalize">{tier}</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-24 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-indigo-500 h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${stats.total > 0 ? (stats.unlocked / stats.total) * 100 : 0}%`,
                    }}
                  />
                </div>
                <span className="text-xs text-gray-600 w-12 text-right">
                  {stats.unlocked}/{stats.total}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* By Category */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">By Category</h3>
        <div className="space-y-2">
          {progress.byCategory && Object.entries(progress.byCategory).map(([category, stats]) => (
            <div key={category} className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="text-lg">{getCategoryIcon(category)}</span>
                <span className="text-sm text-gray-700 capitalize">{category}</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-24 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-purple-500 h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${stats.total > 0 ? (stats.unlocked / stats.total) * 100 : 0}%`,
                    }}
                  />
                </div>
                <span className="text-xs text-gray-600 w-12 text-right">
                  {stats.unlocked}/{stats.total}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AchievementProgress;
